import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button, LoadingOverlay, Stack } from "@mantine/core";
import { IconBuilding } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import { useAuth } from "../../context/AuthContext";
import { useFeatureAccess } from "../../context/FeatureAccessContext";
import AccountCreationLayout from "./components/AccountCreationLayout";
import OnboardingStepCard from "./components/OnboardingStepCard";
import WizardNav from "./components/WizardNav";
import AboutYourselfStepContent from "./steps/AboutYourselfStepContent";
import BusinessBasicsStepContent from "./steps/BusinessBasicsStepContent";
import AboutYourBusinessStepContent from "./steps/AboutYourBusinessStepContent";
import BusinessHoursStepContent from "./steps/BusinessHoursStepContent";
import BusinessGoalsStepContent from "./steps/BusinessGoalsStepContent";
import {
  OnboardingData,
  OnboardingStage,
  AboutYourselfData,
  BusinessBasicsData,
  AboutYourBusinessData,
  BusinessHoursData,
  DayHours,
  BusinessGoalsData,
  createEmptyOnboardingData,
  STEP_CONFIGS,
  STAGE_ORDER,
  getNextStage,
  getPreviousStage,
  getStageNumber,
  aboutYourBusinessToCreateSalonRequest,
  businessHoursToApiFormat,
  businessHoursToStaffFormat,
  QUESTION_KEYS,
  getAnswerTypeForQuestion,
  getStageForQuestion,
  convertTo24HourFormat,
  isStageValid,
  getValidOnboardingStage,
  normalizeBusinessVerticalValue,
} from "./types/onboarding";
import styles from "./OnboardingPage.module.css";

const TOTAL_STEPS = STAGE_ORDER.length;

function AccountCreationWizard() {
  const navigate = useNavigate();
  const {
    getOnboardingProgress,
    updateOnboardingProgress,
    upsertOnboardingAnswer,
    getOnboardingQuestions,
    createSalon,
    updateSalon,
    updateStaff,
  } = useApp();
  const { currentUser: currentCognitoUser } = useAuth();
  const { access, isLoading: isAccessLoading } = useFeatureAccess();
  const {
    setBusinessInfo,
    businessInfo,
    isMissingSalon,
    refreshAllData,
    availableLocations,
    openLocationPicker,
  } = useSalon() as {
    setBusinessInfo: React.Dispatch<
      React.SetStateAction<Record<string, unknown>>
    >;
    businessInfo: Record<string, unknown> | null;
    isMissingSalon: boolean | null;
    refreshAllData: () => Promise<void>;
    availableLocations: { salon_id: number }[];
    openLocationPicker: () => void;
  };

  const [isLoading, setIsLoading] = useState(true);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>(
    createEmptyOnboardingData(),
  );
  const [salonId, setSalonId] = useState<string | null>(null);

  // Live write-target (data-corruption fix): every save derives the target
  // salon id from the CONTEXT value at call time via this ref, never from the
  // salonId state captured when a callback closure was created — captured ids
  // are the proven stale-write channel (a re-resolved salon left old ids in
  // closures, corrupting the ORIGINAL salon's row). salonId state remains for
  // flow control ("does a salon exist yet"); the ref falls back to it while
  // the context has no salon (pre-creation / mid-creation).
  const liveSalonIdRef = useRef<string | null>(null);
  liveSalonIdRef.current = businessInfo?.id ? String(businessInfo.id) : null;

  // Ref to track pending salon creation promise
  const pendingSalonCreationRef = useRef<Promise<string | null> | null>(null);

  // Ref to track if we're in the middle of a stage transition
  // This prevents unnecessary reloads when businessInfo updates after create/update
  const isTransitioningRef = useRef(false);

  // Ref to prevent multiple simultaneous navigation/update operations
  const isProcessingRef = useRef(false);

  // Ref to track all pending async operations from handleNext
  // This allows handleFinish to wait for all operations to complete
  const pendingOperationsRef = useRef<Promise<unknown>[]>([]);

  // State to show loading when finish is clicked and waiting for pending operations
  const [isFinishing, setIsFinishing] = useState(false);

  // Ref to track if we've done the initial load
  // This prevents reloading when businessInfo updates after salon creation
  const hasInitialLoadRef = useRef(false);

  // Ref to store debounce timers for saving answers
  // Key is questionKey to allow independent debouncing per field
  const saveDebounceTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Ensure currentStage is always valid to prevent TypeError
  const currentStage = getValidOnboardingStage(onboardingData.stage);
  const stepConfig = STEP_CONFIGS[currentStage];
  const currentStepNumber = getStageNumber(currentStage);

  // Block access when initial onboarding is complete (route is only for 5-step wizard)
  useEffect(() => {
    if (isAccessLoading || !access) return;
    if (access.onboarding?.initial_onboarding === "complete") {
      const progressiveComplete =
        access.onboarding?.progressive_employees === "complete" &&
        access.onboarding?.progressive_services === "complete" &&
        access.onboarding?.progressive_payments === "complete";
      navigate(progressiveComplete ? "/admin/calendar" : "/onboarding", {
        replace: true,
      });
    }
  }, [access, isAccessLoading, navigate]);

  // Load onboarding progress and all saved answers on mount
  useEffect(() => {
    // Skip reload if we're in the middle of a transition
    if (isTransitioningRef.current) {
      return;
    }

    // Skip reload if we've already done the initial load
    // Only reload if businessInfo goes from null to a value (salon was just created)
    // This prevents full component refresh when businessInfo updates after salon creation/update
    if (hasInitialLoadRef.current) {
      // Only reload if businessInfo was null and now has a value (salon just created)
      // We'll handle this case in handleNext when salon is created
      return;
    }

    // Wait for SalonContext to resolve whether a salon exists. Once one does,
    // progress MUST be queried by its uuid: the staff-keyed (no-arg) row is
    // the ORIGINAL salon's, and with the multi-location flag ON it would
    // resurrect the wrong location's wizard state after a location switch.
    if (isMissingSalon === null) {
      return;
    }

    const loadOnboardingData = async () => {
      try {
        setIsLoading(true);

        // Get current progress (by salon uuid when a salon exists; see above)
        const salonUuid = businessInfo?.id ? String(businessInfo.id) : null;
        const multiLocationOn = (access?.available_locations?.length ?? 0) > 0;
        let progress: Awaited<ReturnType<typeof getOnboardingProgress>> = null;
        if (salonUuid) {
          progress = await getOnboardingProgress(salonUuid);
        }
        if (!progress && !multiLocationOn) {
          // Legacy single-location fallback: staff-keyed row (covers rows
          // created before the salon existed). Never taken when the flag is
          // ON — it would return another location's onboarding.
          progress = await getOnboardingProgress();
        }

        let currentSalonId: string | null = null;
        // Use businessInfo.id (salon UUID from salon response) as primary source
        // progress.id is the onboarding record ID, not the salon UUID
        // progress.salonId is the numeric salon ID, not the UUID we need
        // When no salon exists, don't set salonId - API will use authenticated user's staffId from token
        if (businessInfo?.id) {
          currentSalonId = String(businessInfo.id);
          setSalonId(currentSalonId);
        } else {
          // No salon exists yet - set to null so API uses authenticated user's staffId
          setSalonId(null);
        }

        // Set the current stage from initialOnboardingStage if available
        // This allows us to skip to the correct stage when resuming onboarding
        // Use runtime validation to prevent TypeError if API returns invalid stage
        if (progress && progress.initialOnboardingStage) {
          const validStage = getValidOnboardingStage(
            progress.initialOnboardingStage,
            STAGE_ORDER[0],
          );
          setOnboardingData((prev) => ({
            ...prev,
            stage: validStage,
          }));
        }

        // Load all saved answers in a single API call
        const allAnswers: Record<string, string> = {};
        try {
          const savedAnswers = await getOnboardingQuestions(
            currentSalonId || undefined,
          );
          // savedAnswers is an array of OnboardingSavedAnswer objects
          if (Array.isArray(savedAnswers)) {
            for (const answer of savedAnswers) {
              if (answer.answerValue) {
                allAnswers[answer.questionKey] = answer.answerValue;
              }
            }
          }
        } catch (err) {
          // Silently handle missing saved answers
        }

        // Populate form data from saved answers
        setOnboardingData((prev) => {
          // Build updated nested objects immutably
          // About Yourself (Stage 1)
          const aboutYourselfUpdates: Partial<AboutYourselfData> = {};
          if (allAnswers[QUESTION_KEYS.WHATS_YOUR_TITLE]) {
            aboutYourselfUpdates.title =
              allAnswers[QUESTION_KEYS.WHATS_YOUR_TITLE];
          }
          if (allAnswers[QUESTION_KEYS.HOW_DID_YOU_HEAR_ABOUT_US]) {
            aboutYourselfUpdates.referralSources =
              allAnswers[QUESTION_KEYS.HOW_DID_YOU_HEAR_ABOUT_US].split(",");
          }

          // Business Basics (Stage 2)
          const businessBasicsUpdates: Partial<BusinessBasicsData> = {};
          if (allAnswers[QUESTION_KEYS.WHAT_TYPE_OF_BUSINESS]) {
            businessBasicsUpdates.businessType =
              allAnswers[QUESTION_KEYS.WHAT_TYPE_OF_BUSINESS];
          }
          const rawVertical =
            allAnswers[QUESTION_KEYS.WHAT_IS_YOUR_BUSINESS_VERTICAL];
          if (rawVertical) {
            businessBasicsUpdates.businessVertical =
              normalizeBusinessVerticalValue(rawVertical) ?? rawVertical;
          }
          if (
            !businessBasicsUpdates.businessVertical &&
            allAnswers[QUESTION_KEYS.WHAT_TYPE_OF_BUSINESS]
          ) {
            const legacy = allAnswers[QUESTION_KEYS.WHAT_TYPE_OF_BUSINESS];
            const nv = normalizeBusinessVerticalValue(legacy);
            if (nv) {
              businessBasicsUpdates.businessVertical = nv;
            }
          }
          if (allAnswers[QUESTION_KEYS.WHAT_IS_YOUR_STAFFING_MODEL]) {
            businessBasicsUpdates.staffingModel =
              allAnswers[QUESTION_KEYS.WHAT_IS_YOUR_STAFFING_MODEL];
          }
          if (allAnswers[QUESTION_KEYS.HOW_MANY_LOCATIONS_DO_YOU_HAVE]) {
            businessBasicsUpdates.locationCount =
              allAnswers[QUESTION_KEYS.HOW_MANY_LOCATIONS_DO_YOU_HAVE];
          }
          if (allAnswers[QUESTION_KEYS.WHAT_IS_YOUR_ESTIMATED_ANNUAL_REVENUE]) {
            businessBasicsUpdates.revenueRange =
              allAnswers[QUESTION_KEYS.WHAT_IS_YOUR_ESTIMATED_ANNUAL_REVENUE];
          }
          if (allAnswers[QUESTION_KEYS.WHAT_IS_YOUR_CURRENT_SOLUTION]) {
            businessBasicsUpdates.currentSolution =
              allAnswers[QUESTION_KEYS.WHAT_IS_YOUR_CURRENT_SOLUTION];
          }

          // About Your Business (Stage 3)
          const aboutYourBusinessUpdates: Partial<AboutYourBusinessData> = {};
          if (allAnswers[QUESTION_KEYS.WHATS_YOUR_BUSINESS_NAME]) {
            aboutYourBusinessUpdates.businessName =
              allAnswers[QUESTION_KEYS.WHATS_YOUR_BUSINESS_NAME];
          }
          if (allAnswers[QUESTION_KEYS.WHATS_YOUR_BUSINESS_ADDRESS]) {
            aboutYourBusinessUpdates.addressLine1 =
              allAnswers[QUESTION_KEYS.WHATS_YOUR_BUSINESS_ADDRESS];
          }
          if (allAnswers[QUESTION_KEYS.BUSINESS_ADDRESS_LINE_2]) {
            aboutYourBusinessUpdates.addressLine2 =
              allAnswers[QUESTION_KEYS.BUSINESS_ADDRESS_LINE_2];
          }
          if (allAnswers[QUESTION_KEYS.BUSINESS_CITY]) {
            aboutYourBusinessUpdates.city =
              allAnswers[QUESTION_KEYS.BUSINESS_CITY];
          }
          if (allAnswers[QUESTION_KEYS.BUSINESS_STATE]) {
            aboutYourBusinessUpdates.state =
              allAnswers[QUESTION_KEYS.BUSINESS_STATE];
          }
          if (allAnswers[QUESTION_KEYS.BUSINESS_ZIP_CODE]) {
            aboutYourBusinessUpdates.zipCode =
              allAnswers[QUESTION_KEYS.BUSINESS_ZIP_CODE];
          }
          if (allAnswers[QUESTION_KEYS.BUSINESS_PHONE_COUNTRY_CODE]) {
            aboutYourBusinessUpdates.countryCode =
              allAnswers[QUESTION_KEYS.BUSINESS_PHONE_COUNTRY_CODE];
          }
          if (allAnswers[QUESTION_KEYS.BUSINESS_PHONE_AREA_CODE]) {
            aboutYourBusinessUpdates.areaCode =
              allAnswers[QUESTION_KEYS.BUSINESS_PHONE_AREA_CODE];
          }
          if (allAnswers[QUESTION_KEYS.BUSINESS_PHONE_NUMBER]) {
            aboutYourBusinessUpdates.phoneNumber =
              allAnswers[QUESTION_KEYS.BUSINESS_PHONE_NUMBER];
          }
          if (allAnswers[QUESTION_KEYS.BUSINESS_PHONE_NUMBER_EXT]) {
            aboutYourBusinessUpdates.phoneNumberExt =
              allAnswers[QUESTION_KEYS.BUSINESS_PHONE_NUMBER_EXT];
          }
          if (allAnswers[QUESTION_KEYS.HOW_MANY_EMPLOYEES_DO_YOU_HAVE]) {
            aboutYourBusinessUpdates.employeeCount =
              allAnswers[QUESTION_KEYS.HOW_MANY_EMPLOYEES_DO_YOU_HAVE];
          }
          if (allAnswers[QUESTION_KEYS.WHAT_IS_YOUR_TIMEZONE]) {
            aboutYourBusinessUpdates.timezone =
              allAnswers[QUESTION_KEYS.WHAT_IS_YOUR_TIMEZONE];
          }

          // Business Goals (Stage 5)
          const businessGoalsUpdates: Partial<BusinessGoalsData> = {};
          if (allAnswers[QUESTION_KEYS.BUSINESS_GOALS]) {
            businessGoalsUpdates.selectedGoals =
              allAnswers[QUESTION_KEYS.BUSINESS_GOALS].split(",");
          }

          // Business Hours (Stage 4) - Load from saved answers
          // Only load times if the day is open (preserves user's time changes)
          const businessHoursUpdates: Partial<BusinessHoursData> = {};
          const loadDayHours = (
            dayKey: keyof BusinessHoursData,
            isOpenKey: string,
            openTimeKey: string,
            endTimeKey: string,
          ) => {
            const isOpen = allAnswers[isOpenKey] === "true";
            const openTime = allAnswers[openTimeKey]
              ? convertTo24HourFormat(allAnswers[openTimeKey])
              : "";
            const endTime = allAnswers[endTimeKey]
              ? convertTo24HourFormat(allAnswers[endTimeKey])
              : "";

            // Only update if we have saved data for this day
            if (allAnswers[isOpenKey] !== undefined) {
              businessHoursUpdates[dayKey] = {
                isOpen,
                // Only use saved times if the day is open
                startTime:
                  isOpen && openTime
                    ? openTime
                    : prev.businessHours[dayKey].startTime,
                endTime:
                  isOpen && endTime
                    ? endTime
                    : prev.businessHours[dayKey].endTime,
              };
            }
          };

          loadDayHours(
            "sunday",
            QUESTION_KEYS.SUNDAY_IS_OPEN,
            QUESTION_KEYS.SUNDAY_OPEN_TIME,
            QUESTION_KEYS.SUNDAY_END_TIME,
          );
          loadDayHours(
            "monday",
            QUESTION_KEYS.MONDAY_IS_OPEN,
            QUESTION_KEYS.MONDAY_OPEN_TIME,
            QUESTION_KEYS.MONDAY_END_TIME,
          );
          loadDayHours(
            "tuesday",
            QUESTION_KEYS.TUESDAY_IS_OPEN,
            QUESTION_KEYS.TUESDAY_OPEN_TIME,
            QUESTION_KEYS.TUESDAY_END_TIME,
          );
          loadDayHours(
            "wednesday",
            QUESTION_KEYS.WEDNESDAY_IS_OPEN,
            QUESTION_KEYS.WEDNESDAY_OPEN_TIME,
            QUESTION_KEYS.WEDNESDAY_END_TIME,
          );
          loadDayHours(
            "thursday",
            QUESTION_KEYS.THURSDAY_IS_OPEN,
            QUESTION_KEYS.THURSDAY_OPEN_TIME,
            QUESTION_KEYS.THURSDAY_END_TIME,
          );
          loadDayHours(
            "friday",
            QUESTION_KEYS.FRIDAY_IS_OPEN,
            QUESTION_KEYS.FRIDAY_OPEN_TIME,
            QUESTION_KEYS.FRIDAY_END_TIME,
          );
          loadDayHours(
            "saturday",
            QUESTION_KEYS.SATURDAY_IS_OPEN,
            QUESTION_KEYS.SATURDAY_OPEN_TIME,
            QUESTION_KEYS.SATURDAY_END_TIME,
          );

          // Build the updated state immutably
          return {
            ...prev,
            aboutYourself:
              Object.keys(aboutYourselfUpdates).length > 0
                ? { ...prev.aboutYourself, ...aboutYourselfUpdates }
                : prev.aboutYourself,
            businessBasics:
              Object.keys(businessBasicsUpdates).length > 0
                ? { ...prev.businessBasics, ...businessBasicsUpdates }
                : prev.businessBasics,
            aboutYourBusiness:
              Object.keys(aboutYourBusinessUpdates).length > 0
                ? { ...prev.aboutYourBusiness, ...aboutYourBusinessUpdates }
                : prev.aboutYourBusiness,
            businessHours:
              Object.keys(businessHoursUpdates).length > 0
                ? { ...prev.businessHours, ...businessHoursUpdates }
                : prev.businessHours,
            businessGoals:
              Object.keys(businessGoalsUpdates).length > 0
                ? { ...prev.businessGoals, ...businessGoalsUpdates }
                : prev.businessGoals,
          };
        });

        // If salon exists, load business info for hours
        // Convert from 12-hour API format to 24-hour format for internal use
        // This will merge with saved answers (saved answers take precedence for times if they exist)
        if (currentSalonId && businessInfo) {
          setOnboardingData((prev) => {
            // Helper to merge businessInfo hours with existing saved answers
            const mergeDayHours = (
              dayKey: keyof BusinessHoursData,
              isOpenKey: string,
              openTimeKey: string,
              endTimeKey: string,
            ): DayHours => {
              // Use saved answers if they exist, otherwise use businessInfo
              const savedIsOpen = allAnswers[isOpenKey] === "true";
              const savedOpenTime = allAnswers[openTimeKey]
                ? convertTo24HourFormat(allAnswers[openTimeKey])
                : "";
              const savedEndTime = allAnswers[endTimeKey]
                ? convertTo24HourFormat(allAnswers[endTimeKey])
                : "";

              // If we have saved answers, use them (they preserve user's changes)
              if (allAnswers[isOpenKey] !== undefined) {
                return {
                  isOpen: savedIsOpen,
                  startTime:
                    savedOpenTime || prev.businessHours[dayKey].startTime,
                  endTime: savedEndTime || prev.businessHours[dayKey].endTime,
                };
              }

              // Otherwise, use businessInfo from API
              return {
                isOpen: (businessInfo as any)[isOpenKey] || false,
                startTime: convertTo24HourFormat(
                  (businessInfo as any)[openTimeKey] || "",
                ),
                endTime: convertTo24HourFormat(
                  (businessInfo as any)[endTimeKey] || "",
                ),
              };
            };

            return {
              ...prev,
              businessHours: {
                sunday: mergeDayHours(
                  "sunday",
                  QUESTION_KEYS.SUNDAY_IS_OPEN,
                  QUESTION_KEYS.SUNDAY_OPEN_TIME,
                  QUESTION_KEYS.SUNDAY_END_TIME,
                ),
                monday: mergeDayHours(
                  "monday",
                  QUESTION_KEYS.MONDAY_IS_OPEN,
                  QUESTION_KEYS.MONDAY_OPEN_TIME,
                  QUESTION_KEYS.MONDAY_END_TIME,
                ),
                tuesday: mergeDayHours(
                  "tuesday",
                  QUESTION_KEYS.TUESDAY_IS_OPEN,
                  QUESTION_KEYS.TUESDAY_OPEN_TIME,
                  QUESTION_KEYS.TUESDAY_END_TIME,
                ),
                wednesday: mergeDayHours(
                  "wednesday",
                  QUESTION_KEYS.WEDNESDAY_IS_OPEN,
                  QUESTION_KEYS.WEDNESDAY_OPEN_TIME,
                  QUESTION_KEYS.WEDNESDAY_END_TIME,
                ),
                thursday: mergeDayHours(
                  "thursday",
                  QUESTION_KEYS.THURSDAY_IS_OPEN,
                  QUESTION_KEYS.THURSDAY_OPEN_TIME,
                  QUESTION_KEYS.THURSDAY_END_TIME,
                ),
                friday: mergeDayHours(
                  "friday",
                  QUESTION_KEYS.FRIDAY_IS_OPEN,
                  QUESTION_KEYS.FRIDAY_OPEN_TIME,
                  QUESTION_KEYS.FRIDAY_END_TIME,
                ),
                saturday: mergeDayHours(
                  "saturday",
                  QUESTION_KEYS.SATURDAY_IS_OPEN,
                  QUESTION_KEYS.SATURDAY_OPEN_TIME,
                  QUESTION_KEYS.SATURDAY_END_TIME,
                ),
              },
            };
          });
        }
      } catch (error) {
        // Error loading onboarding data - will redirect to account creation
      } finally {
        setIsLoading(false);
        hasInitialLoadRef.current = true;
      }
    };

    loadOnboardingData();
  }, [
    getOnboardingProgress,
    getOnboardingQuestions,
    isMissingSalon,
    businessInfo?.id,
  ]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      // Clear all pending debounce timers
      saveDebounceTimersRef.current.forEach((timer) => {
        clearTimeout(timer);
      });
      saveDebounceTimersRef.current.clear();
    };
  }, []);

  // Fire-and-forget answer save (async, no blocking) with debouncing
  // Debounces saves by 1 second to avoid saving on every keystroke
  const saveAnswerAsync = useCallback(
    (questionKey: string, questionValue: string, answerValue: string) => {
      // Skip saving if answerValue is empty or undefined
      // This prevents API errors when users are typing (e.g., phone numbers)
      if (!answerValue || answerValue.trim() === "") {
        // Clear any pending debounce timer for this field
        const existingTimer = saveDebounceTimersRef.current.get(questionKey);
        if (existingTimer) {
          clearTimeout(existingTimer);
          saveDebounceTimersRef.current.delete(questionKey);
        }
        return;
      }

      // Clear any existing debounce timer for this questionKey
      const existingTimer = saveDebounceTimersRef.current.get(questionKey);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Set new debounce timer (1 second delay)
      const timer = setTimeout(() => {
        // Determine the correct answerType and stage for this question
        const answerType = getAnswerTypeForQuestion(questionKey);
        const stage = getStageForQuestion(questionKey);

        // Fire and forget - don't await
        // Target the LIVE salon id (context at call time), not the captured
        // state — see liveSalonIdRef.
        upsertOnboardingAnswer(
          { questionKey, questionValue, answerValue, answerType, stage },
          (liveSalonIdRef.current ?? salonId) || undefined,
        ).catch(() => {
          // Silently handle answer save errors
        });

        // Clean up timer reference
        saveDebounceTimersRef.current.delete(questionKey);
      }, 1000); // 1 second debounce delay

      // Store timer reference
      saveDebounceTimersRef.current.set(questionKey, timer);
    },
    [upsertOnboardingAnswer, salonId],
  );

  const handleAboutYourselfChange = useCallback(
    (
      data: Partial<AboutYourselfData>,
      questionKey?: string,
      questionValue?: string,
      answerValue?: string,
    ) => {
      setOnboardingData((prev) => ({
        ...prev,
        aboutYourself: { ...prev.aboutYourself, ...data },
      }));

      if (questionKey && questionValue && answerValue) {
        saveAnswerAsync(questionKey, questionValue, answerValue);
      }
    },
    [saveAnswerAsync],
  );

  const handleBusinessBasicsChange = useCallback(
    (
      data: Partial<BusinessBasicsData>,
      questionKey?: string,
      questionValue?: string,
      answerValue?: string,
    ) => {
      setOnboardingData((prev) => ({
        ...prev,
        businessBasics: { ...prev.businessBasics, ...data },
      }));

      if (questionKey && questionValue && answerValue) {
        saveAnswerAsync(questionKey, questionValue, answerValue);
      }
    },
    [saveAnswerAsync],
  );

  const handleAboutYourBusinessChange = useCallback(
    (
      data: Partial<AboutYourBusinessData>,
      questionKey?: string,
      questionValue?: string,
      answerValue?: string,
    ) => {
      setOnboardingData((prev) => ({
        ...prev,
        aboutYourBusiness: { ...prev.aboutYourBusiness, ...data },
      }));

      if (questionKey && questionValue && answerValue !== undefined) {
        saveAnswerAsync(questionKey, questionValue, answerValue);
      }
    },
    [saveAnswerAsync],
  );

  const handleBusinessHoursChange = useCallback(
    (
      data: Partial<BusinessHoursData>,
      questionKey?: string,
      questionValue?: string,
      answerValue?: string,
    ) => {
      setOnboardingData((prev) => ({
        ...prev,
        businessHours: { ...prev.businessHours, ...data },
      }));

      if (questionKey && questionValue && answerValue !== undefined) {
        saveAnswerAsync(questionKey, questionValue, answerValue);
      }
    },
    [saveAnswerAsync],
  );

  const handleBusinessGoalsChange = useCallback(
    (
      data: Partial<BusinessGoalsData>,
      questionKey?: string,
      questionValue?: string,
      answerValue?: string,
    ) => {
      setOnboardingData((prev) => ({
        ...prev,
        businessGoals: { ...prev.businessGoals, ...data },
      }));

      if (questionKey && questionValue && answerValue) {
        saveAnswerAsync(questionKey, questionValue, answerValue);
      }
    },
    [saveAnswerAsync],
  );

  const handleBack = useCallback(() => {
    // Prevent multiple simultaneous back operations
    if (isProcessingRef.current) {
      return;
    }

    const prevStage = getPreviousStage(currentStage);
    if (prevStage) {
      // Reset transition flag when going back
      isTransitioningRef.current = false;
      setOnboardingData((prev) => ({
        ...prev,
        stage: prevStage,
      }));
    }
  }, [currentStage]);

  const handleNext = useCallback(() => {
    // Prevent multiple simultaneous next operations
    if (isProcessingRef.current) {
      return;
    }

    // Validate required fields before proceeding
    if (!isStageValid(currentStage, onboardingData)) {
      return; // Don't proceed if validation fails
    }

    const nextStage = getNextStage(currentStage);
    if (!nextStage) return;

    // Mark that we're processing to prevent multiple calls
    isProcessingRef.current = true;
    isTransitioningRef.current = true;

    // Ensure timezone is saved as an answer when clicking Next
    // This handles the case where the default timezone was set automatically
    if (
      currentStage === "about_your_business" &&
      onboardingData.aboutYourBusiness.timezone
    ) {
      // Save timezone answer (ensures default value is sent to backend)
      saveAnswerAsync(
        QUESTION_KEYS.WHAT_IS_YOUR_TIMEZONE,
        "What is your Timezone?",
        onboardingData.aboutYourBusiness.timezone,
      );
    }

    // Immediately transition to the next stage
    setOnboardingData((prev) => ({
      ...prev,
      stage: nextStage,
    }));

    // Reset processing flag immediately after state update
    // The UI transition is synchronous, so we can allow the next click immediately
    // isTransitioningRef stays true to prevent reloads during async operations
    isProcessingRef.current = false;

    // Fire async operations in the background and track them
    const operationPromise = (async () => {
      try {
        // Stage 3: Create or update salon
        if (currentStage === "about_your_business") {
          // Live context id at call time (falls back to captured state only
          // while the context has no salon) — see liveSalonIdRef.
          const writeSalonId = liveSalonIdRef.current ?? salonId;
          if (!writeSalonId) {
            // No salon exists - create a new one
            const salonCreationPromise = (async () => {
              const salonData = aboutYourBusinessToCreateSalonRequest(
                onboardingData.aboutYourBusiness,
                onboardingData.businessBasics.businessType || undefined,
                onboardingData.businessBasics.businessVertical || undefined,
                onboardingData.businessBasics.staffingModel || undefined,
              );

              const response = await createSalon(salonData);
              if (response?.id) {
                const newSalonId = String(response.id);
                setSalonId(newSalonId);
                setBusinessInfo(response);

                // Update progress with new salonId
                const progressPromise = updateOnboardingProgress(
                  {
                    section: "initial_onboarding",
                    state: "inprogress",
                    stage: nextStage,
                  },
                  newSalonId,
                ).catch((err: unknown) => {
                  console.error(
                    "Onboarding: failed to update progress after salon creation",
                    err,
                  );
                });

                // Track this operation
                pendingOperationsRef.current.push(progressPromise);

                // Remove from tracking and reset transition flag after operations complete
                progressPromise.finally(() => {
                  // Remove completed operation from tracking
                  pendingOperationsRef.current =
                    pendingOperationsRef.current.filter(
                      (p) => p !== progressPromise,
                    );
                  // Reset transition flag after a short delay to allow state to settle
                  setTimeout(() => {
                    isTransitioningRef.current = false;
                  }, 100);
                });

                return newSalonId;
              }
              return null;
            })();

            pendingSalonCreationRef.current = salonCreationPromise;
            await salonCreationPromise;
          } else {
            // Salon already exists - update it instead of creating a new one
            const updateData: Record<string, unknown> = {
              id: writeSalonId,
              salon_name: onboardingData.aboutYourBusiness.businessName,
              street_address_1: onboardingData.aboutYourBusiness.addressLine1,
              street_address_2:
                onboardingData.aboutYourBusiness.addressLine2 || undefined,
              city: onboardingData.aboutYourBusiness.city,
              state: onboardingData.aboutYourBusiness.state,
              zip_code: onboardingData.aboutYourBusiness.zipCode,
              business_model:
                onboardingData.businessBasics.businessType || undefined,
              business_vertical: (() => {
                const v = onboardingData.businessBasics.businessVertical;
                if (!v?.trim()) return undefined;
                return normalizeBusinessVerticalValue(v) ?? v;
              })(),
            };

            if (onboardingData.businessBasics.staffingModel) {
              updateData.staffing_model =
                onboardingData.businessBasics.staffingModel;
            }

            // Persist timezone + phone on the existing-salon path too — the
            // create path always sent them, but this path omitted them, so a
            // support-created location's timezone silently stayed at the DB
            // default (America/New_York) no matter what the wizard showed.
            if (onboardingData.aboutYourBusiness.timezone) {
              updateData.timezone = onboardingData.aboutYourBusiness.timezone;
            }
            if (
              onboardingData.aboutYourBusiness.areaCode &&
              onboardingData.aboutYourBusiness.phoneNumber
            ) {
              updateData.country_code = "1"; // matches the create path
              updateData.area_code = onboardingData.aboutYourBusiness.areaCode;
              updateData.phone_number =
                onboardingData.aboutYourBusiness.phoneNumber;
            }

            const updatePromise = updateSalon(updateData);
            const progressPromise = updateOnboardingProgress(
              {
                section: "initial_onboarding",
                state: "inprogress",
                stage: nextStage,
              },
              writeSalonId,
            ).catch((err: unknown) => {
              console.error(
                "Onboarding: failed to update progress after salon update",
                err,
              );
            });

            // Track both operations
            pendingOperationsRef.current.push(updatePromise, progressPromise);

            const response = await updatePromise;
            if (response) {
              setBusinessInfo((prev: any) => ({ ...prev, ...response }));
            }

            // Remove from tracking and reset transition flag after operations complete
            Promise.all([updatePromise, progressPromise]).finally(() => {
              // Remove completed operations from tracking
              pendingOperationsRef.current =
                pendingOperationsRef.current.filter(
                  (p) => p !== updatePromise && p !== progressPromise,
                );
              // Reset transition flag after a short delay to allow state to settle
              setTimeout(() => {
                isTransitioningRef.current = false;
              }, 100);
            });
          }
        }
        // Stage 4: Update salon hours
        else if (currentStage === "business_hours") {
          // Live context id at call time; wait for pending salon creation if
          // it hasn't completed yet
          let effectiveSalonId = liveSalonIdRef.current ?? salonId;
          if (pendingSalonCreationRef.current && !effectiveSalonId) {
            effectiveSalonId = await pendingSalonCreationRef.current;
            pendingSalonCreationRef.current = null;
          }

          if (effectiveSalonId) {
            const hoursData = businessHoursToApiFormat(
              effectiveSalonId,
              onboardingData.businessHours,
            );
            const updatePromise = updateSalon(hoursData);
            const progressPromise = updateOnboardingProgress(
              {
                section: "initial_onboarding",
                state: "inprogress",
                stage: nextStage,
              },
              effectiveSalonId,
            ).catch((err: unknown) => {
              console.error(
                "Onboarding: failed to update progress after hours update",
                err,
              );
            });

            // Back-populate salon hours into the owner's working hours
            // During onboarding the owner is created with no working hours,
            // so we copy the salon hours as their default schedule
            const ownerStaffId = currentCognitoUser?.userId;
            const staffHoursPromise = ownerStaffId
              ? updateStaff(
                  businessHoursToStaffFormat(
                    ownerStaffId,
                    onboardingData.businessHours,
                  ),
                ).catch((err: unknown) => {
                  console.error(
                    "Onboarding: failed to back-populate owner working hours",
                    err,
                  );
                })
              : Promise.resolve();

            // Track all operations
            pendingOperationsRef.current.push(
              updatePromise,
              progressPromise,
              staffHoursPromise,
            );

            const response = await updatePromise;
            if (response) {
              setBusinessInfo((prev: any) => ({ ...prev, ...response }));
            }

            // Remove from tracking and reset transition flag after operations complete
            Promise.all([
              updatePromise,
              progressPromise,
              staffHoursPromise,
            ]).finally(() => {
              // Remove completed operations from tracking
              pendingOperationsRef.current =
                pendingOperationsRef.current.filter(
                  (p) =>
                    p !== updatePromise &&
                    p !== progressPromise &&
                    p !== staffHoursPromise,
                );
              // Reset transition flag after a short delay to allow state to settle
              setTimeout(() => {
                isTransitioningRef.current = false;
              }, 100);
            });
          }
        }
        // Other stages: Just update progress
        else {
          const progressPromise = updateOnboardingProgress(
            {
              section: "initial_onboarding",
              state: "inprogress",
              stage: nextStage,
            },
            (liveSalonIdRef.current ?? salonId) || undefined,
          ).catch((err: unknown) => {
            console.error("Onboarding: failed to update progress", err);
          });

          // Track this operation
          pendingOperationsRef.current.push(progressPromise);

          // Remove from tracking and reset transition flag after operation completes
          progressPromise.finally(() => {
            // Remove completed operation from tracking
            pendingOperationsRef.current = pendingOperationsRef.current.filter(
              (p) => p !== progressPromise,
            );
            // Reset transition flag after a short delay to allow state to settle
            setTimeout(() => {
              isTransitioningRef.current = false;
            }, 100);
          });
        }
      } catch (error) {
        // Error in background save - reset transition flag
        isTransitioningRef.current = false;
      }
    })();

    // Track the main operation and ensure it's cleaned up when done
    pendingOperationsRef.current.push(operationPromise);
    operationPromise.finally(() => {
      // Remove completed operation from tracking
      pendingOperationsRef.current = pendingOperationsRef.current.filter(
        (p) => p !== operationPromise,
      );
    });
  }, [
    currentStage,
    salonId,
    onboardingData,
    createSalon,
    updateSalon,
    updateStaff,
    updateOnboardingProgress,
    setBusinessInfo,
    saveAnswerAsync,
    currentCognitoUser,
  ]);

  const handleFinish = useCallback(async () => {
    // Prevent multiple finish clicks
    if (isProcessingRef.current || isFinishing) {
      return;
    }

    // Mark that we're finishing
    setIsFinishing(true);
    isProcessingRef.current = true;

    try {
      // Wait for all pending operations from back/forward navigation to complete
      // This ensures all data is saved before we complete onboarding
      const pendingOps = pendingOperationsRef.current.filter((p) => p !== null);
      if (pendingOps.length > 0) {
        await Promise.allSettled(pendingOps);
      }

      // Clear the pending operations array
      pendingOperationsRef.current = [];

      // Live context id at call time; wait for pending salon creation if it
      // hasn't completed yet
      let effectiveSalonId = liveSalonIdRef.current ?? salonId;
      if (pendingSalonCreationRef.current && !effectiveSalonId) {
        effectiveSalonId = await pendingSalonCreationRef.current;
        pendingSalonCreationRef.current = null;
      }

      // Fire off the onboarding completion update (await it)
      // This ensures the backend is updated before navigation
      await updateOnboardingProgress(
        {
          section: "initial_onboarding",
          state: "complete",
          stage: "business_goals",
        },
        effectiveSalonId || undefined,
      );

      // Refresh all context data after initial onboarding completes
      // This fetches employees, services, and updated salon info
      await refreshAllData();

      // Navigate to onboarding after update completes
      navigate("/onboarding");
    } catch (error) {
      // On error, still navigate but log the error
      console.error("Error completing onboarding:", error);
      navigate("/onboarding");
    } finally {
      // Reset flags after a delay
      setTimeout(() => {
        isProcessingRef.current = false;
        setIsFinishing(false);
      }, 1000);
    }
  }, [
    salonId,
    navigate,
    updateOnboardingProgress,
    isFinishing,
    refreshAllData,
  ]);

  // Multi-location escape hatch (DESIGN_DELTA_2026-07-23 §4): the owner-login
  // redirect into this wizard is NOT a hard gate — a multi-location user can
  // open the LocationPicker mid-onboarding and switch back to a completed
  // location. Hidden for single-location users / when the flag is OFF
  // (availableLocations is then [] and nothing renders).
  const switchLocationAffordance =
    (availableLocations?.length ?? 0) > 1 ? (
      <Button
        variant="subtle"
        color="gray"
        leftSection={<IconBuilding size={18} stroke={1.5} />}
        onClick={openLocationPicker}
      >
        Switch location
      </Button>
    ) : null;

  const renderStepContent = () => {
    switch (currentStage) {
      case "about_yourself":
        return (
          <AboutYourselfStepContent
            data={onboardingData.aboutYourself}
            onChange={handleAboutYourselfChange}
          />
        );
      case "business_basics":
        return (
          <BusinessBasicsStepContent
            data={onboardingData.businessBasics}
            onChange={handleBusinessBasicsChange}
          />
        );
      case "about_your_business":
        return (
          <AboutYourBusinessStepContent
            data={onboardingData.aboutYourBusiness}
            onChange={handleAboutYourBusinessChange}
            businessBasicsData={onboardingData.businessBasics}
            onBusinessBasicsChange={handleBusinessBasicsChange}
          />
        );
      case "business_hours":
        return (
          <BusinessHoursStepContent
            data={onboardingData.businessHours}
            onChange={handleBusinessHoursChange}
          />
        );
      case "business_goals":
        return (
          <BusinessGoalsStepContent
            data={onboardingData.businessGoals}
            onChange={handleBusinessGoalsChange}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <AccountCreationLayout
        currentStep={1}
        totalSteps={TOTAL_STEPS}
        headerRight={switchLocationAffordance}
      >
        <LoadingOverlay visible />
      </AccountCreationLayout>
    );
  }

  // Safety check: if stepConfig is undefined (shouldn't happen with validation above),
  // fallback to first stage config and show loading
  if (!stepConfig) {
    // Invalid onboarding stage - fallback to first stage
    return (
      <AccountCreationLayout
        currentStep={1}
        totalSteps={TOTAL_STEPS}
        headerRight={switchLocationAffordance}
      >
        <LoadingOverlay visible />
      </AccountCreationLayout>
    );
  }

  return (
    <AccountCreationLayout
      currentStep={currentStepNumber}
      totalSteps={TOTAL_STEPS}
      headerRight={switchLocationAffordance}
    >
      <Stack gap="lg" className={styles.contentWrapper}>
        <OnboardingStepCard
          title={stepConfig.title}
          subtitle={stepConfig.subtitle}
        >
          {renderStepContent()}
        </OnboardingStepCard>

        <WizardNav
          currentStep={currentStepNumber}
          totalSteps={TOTAL_STEPS}
          onBack={handleBack}
          onNext={handleNext}
          onFinish={handleFinish}
          isLoading={isFinishing}
          isNextDisabled={
            !isStageValid(currentStage, onboardingData) || isFinishing
          }
        />
      </Stack>
    </AccountCreationLayout>
  );
}

/**
 * Remount wrapper (data-corruption fix, belt-and-suspenders with
 * liveSalonIdRef): the wizard captures salon identity per mount (salonId
 * state, loaded answers), so when SalonContext re-resolves to a DIFFERENT
 * salon while the wizard is mounted, keying the wizard by salon id remounts
 * it cleanly instead of letting captured state write to the old salon.
 *
 * The one transition that must NOT remount is the wizard's own first-salon
 * creation (businessInfo null -> new id while isMissingSalon was true): a
 * mid-flow remount would race the in-flight progress PUT and bounce the user
 * back a stage. Boot-time first resolution (salon already existed;
 * isMissingSalon never true) DOES adopt the id — the wizard is still in its
 * loading gate then, so that remount is invisible.
 */
export default function AccountCreationPage() {
  const { businessInfo, isMissingSalon } = useSalon() as {
    businessInfo: Record<string, unknown> | null;
    isMissingSalon: boolean | null;
  };
  const trackerRef = useRef<{
    key: string;
    id: string | null;
    sawMissingSalon: boolean;
  }>({ key: "no-salon", id: null, sawMissingSalon: false });
  // NOTE: this ref is mutated during RENDER (below). That is only safe
  // because the mutation is idempotent — re-running the same render (React
  // StrictMode double-invoke, concurrent re-render) computes the same tracker
  // state from the same inputs. Keep it that way: no counters, no toggles.
  const tracker = trackerRef.current;
  const currentId = businessInfo?.id ? String(businessInfo.id) : null;
  if (currentId !== tracker.id) {
    if (currentId != null && (tracker.id != null || !tracker.sawMissingSalon)) {
      // Salon identity changed (id -> different id), or boot-time first
      // resolution: remount on the new identity.
      tracker.key = currentId;
    }
    // else: the wizard just created the first salon — keep the mount alive.
    tracker.id = currentId;
  }
  if (isMissingSalon === true) {
    tracker.sawMissingSalon = true;
  }
  return <AccountCreationWizard key={tracker.key} />;
}
